import {
  useHttpClient,
  useNetworkStatus,
  useToast,
} from '@airship-platform/airship-patterns';
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import { ServiceHistory } from './serviceHistory.models';
import {
  initState,
  serviceHistoryReducer,
  ServiceHistoryState,
} from './serviceHistory.reducer';

interface ServiceHistoryContextProps {
  serviceHistory: ServiceHistory[];
  isLoading: boolean;
  isLoaded: boolean;
  error: string | null;
  fetchServiceHistory: (
    appointmentId: string,
    locationId: string,
    customerId: string,
    options?: { signal?: AbortSignal }
  ) => Promise<void>;
}

const ServiceHistoryContext = createContext<
  ServiceHistoryContextProps | undefined
>(undefined);

type OwnProps = PropsWithChildren<{
  initialState?: Partial<ServiceHistoryState>;
}>;

export const ServiceHistoryProvider: React.FC<OwnProps> = ({
  children,
  initialState,
}) => {
  const [state, dispatch] = useReducer(serviceHistoryReducer, {
    ...initState,
    ...initialState,
  });
  const isOnline = useNetworkStatus();
  const httpClient = useHttpClient();
  const { showToast } = useToast();

  useEffect(() => {
    state.error && showToast(state.error, 'error');
  }, [state.error, showToast]);

  const serviceHistory = useMemo(
    () =>
      state.selectedAppointmentId
        ? state.entities[state.selectedAppointmentId] || []
        : [],
    [state.entities, state.selectedAppointmentId]
  );

  const fetchServiceHistory: ServiceHistoryContextProps['fetchServiceHistory'] =
    useCallback(
      async (appointmentId, locationId, customerId, options) => {
        if (!isOnline()) {
          dispatch({
            type: 'SET_LOADING_STATE',
            payload: { isLoading: false, isLoaded: true },
          });
          return;
        }
        const params = new URLSearchParams({
          status: 'COMPLETED',
          ...(locationId && { locationId }),
          ...(customerId && { customerId }),
        });

        dispatch({
          type: 'FETCH_SERVICE_HISTORY',
          payload: { appointmentId },
        });

        try {
          const serviceHistory = await httpClient.get<ServiceHistory[]>(
            `/service-history/?${params.toString()}`,
            { signal: options?.signal }
          );
          dispatch({
            type: 'FETCH_SERVICE_HISTORY_SUCCESS',
            payload: { appointmentId, serviceHistory },
          });
        } catch (error) {
          if (error instanceof Error && error.message === 'canceled') {
            dispatch({ type: 'FETCH_SERVICE_HISTORY_CANCELLED' });
          } else {
            dispatch({
              type: 'FETCH_SERVICE_HISTORY_FAILURE',
              payload: { error: `${error}` },
            });
          }
        }
      },
      [httpClient, isOnline]
    );

  return (
    <ServiceHistoryContext.Provider
      value={{
        fetchServiceHistory,
        isLoading: state.isLoading,
        isLoaded: state.isLoaded,
        error: state.error,
        serviceHistory,
      }}
    >
      {children}
    </ServiceHistoryContext.Provider>
  );
};

export const useServiceHistory = () => {
  const context = useContext(ServiceHistoryContext);
  if (context === undefined) {
    throw new Error(
      'useServiceHistory must be used within an ServiceHistoryProvider'
    );
  }
  return context;
};
